// Aliased in place of the bare `react-native` import for the WHOLE web
// bundle (see webpack.config.web.js's `resolve.alias['react-native$']`) —
// every `import { Modal, View, ... } from 'react-native'` across the entire
// app resolves here instead of straight to react-native-web, with every
// export re-exported UNCHANGED except `Modal`.
//
// ROOT CAUSE this exists to fix: react-native-web's real `Modal` always
// renders via a `position: fixed` container appended near `document.body`
// (confirmed live by walking the DOM: `position: fixed` with inset-0
// classes, siblings of <body>, several levels above any app-rendered
// content) — a real DOM portal that structurally cannot be nested inside
// AppShell.web.tsx's centered phone-width container, no matter what CSS is
// applied to AppShell itself. ~20 files (ProfileScreen.tsx alone uses
// <Modal> ten times) render dialogs, bottom sheets, and confirmation
// prompts this way — patching each call site individually would be the
// "arbitrary per-screen width" fix explicitly asked against; this is the
// one shared choke point everything already imports through.
//
// The fix: portal into a dedicated node INSIDE the phone-shell (rendered by
// AppShell.web.tsx, supplied here via ModalPortalContext) instead of
// document.body, so every existing <Modal> usage keeps working completely
// unchanged (same props, same children, same "renders above everything
// else in the app" contract) while staying visually confined to the phone
// viewport on desktop. On native this file is never bundled at all — Metro
// resolves the real `react-native` package, so iOS/Android Modal behavior
// (and everything else here) is completely untouched.
import React, { createContext, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as ReactNativeWeb from 'react-native-web';

export const ModalPortalContext = createContext<HTMLElement | null>(null);

type PatchedModalProps = {
  visible?: boolean;
  transparent?: boolean;
  animationType?: 'none' | 'slide' | 'fade';
  onRequestClose?: () => void;
  onShow?: () => void;
  statusBarTranslucent?: boolean;
  presentationStyle?: string;
  hardwareAccelerated?: boolean;
  children?: React.ReactNode;
};

function PatchedModal({ visible = true, onRequestClose, onShow, children }: PatchedModalProps) {
  const portalTarget = useContext(ModalPortalContext);

  useEffect(() => {
    if (visible) onShow?.();
    // Matches native Modal's onShow contract (fires once when it becomes
    // visible) closely enough for this app's usage — none of the ~20 call
    // sites depend on onShow firing again on prop changes besides visibility.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!visible || !onRequestClose) return undefined;
    // Matches native Modal's Android back-button/onRequestClose contract
    // with the web equivalent gesture (Escape key).
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onRequestClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, onRequestClose]);

  if (!visible) return null;

  // Matches real Modal's contract of handing children a full-"screen"
  // container to lay out within (flex/percentage-based children throughout
  // the app already assume this) — "full screen" here means the phone
  // shell's own bounds (via the portal target below), not the browser
  // window.
  const content = (
    <ReactNativeWeb.View
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'auto' }}
    >
      {children}
    </ReactNativeWeb.View>
  );

  if (typeof document === 'undefined') return content;
  return createPortal(content, portalTarget || document.body);
}

// ROOT CAUSE this second patch exists to fix: react-native-web's real `Alert`
// is a hard no-op (`class Alert { static alert() {} }` — see
// node_modules/react-native-web/src/exports/Alert/index.js). Every
// `Alert.alert(title, message, buttons)` call in the app (121 call sites
// across 24 files, confirmed by grep) silently does nothing on web: no
// dialog appears, and — critically — none of the buttons' `onPress`
// callbacks ever fire. This is not a Playwright/automation artifact; a real
// person clicking "Logout" in a real browser sees nothing happen too,
// because ProfileScreen.tsx's confirmSignOut() routes the actual sign-out
// through `Alert.alert(..., [{ text: cancel }, { text: signOut, onPress:
// onSignOut }])` and that onPress is simply never invoked.
//
// The fix: a minimal drop-in Alert.alert(title, message, buttons, options)
// that actually renders a themed confirm dialog and calls the pressed
// button's onPress (or options.onDismiss on backdrop-tap when
// options.cancelable is set) — matching the exact call signature already
// used everywhere, so none of the 24 call-site files need to change.
type AlertButton = {
  text?: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: (value?: string) => void;
};
type AlertOptions = { cancelable?: boolean; onDismiss?: () => void };
type AlertState = { title: string; message?: string; buttons: AlertButton[]; options?: AlertOptions } | null;

let currentAlertState: AlertState = null;
let alertListeners: Array<(state: AlertState) => void> = [];

function setAlertState(state: AlertState) {
  currentAlertState = state;
  alertListeners.forEach(listener => listener(state));
}

class PatchedAlert {
  static alert(title: string, message?: string, buttons?: AlertButton[], options?: AlertOptions) {
    setAlertState({
      title,
      message,
      buttons: buttons && buttons.length > 0 ? buttons : [{ text: 'OK' }],
      options,
    });
  }
}

// Mounted once, inside the phone-shell, by AppShell.web.tsx — always live so
// Alert.alert works from any screen, not just while some other Modal is open.
export function AlertHost() {
  const [state, setState] = useState<AlertState>(currentAlertState);

  useEffect(() => {
    alertListeners.push(setState);
    return () => {
      alertListeners = alertListeners.filter(listener => listener !== setState);
    };
  }, []);

  if (!state) return null;

  const handlePress = (button: AlertButton) => {
    setAlertState(null);
    button.onPress?.();
  };

  const handleBackdropPress = () => {
    if (!state.options?.cancelable) return;
    setAlertState(null);
    state.options.onDismiss?.();
  };

  return (
    <ReactNativeWeb.View style={alertStyles.overlay}>
      <ReactNativeWeb.Pressable style={ReactNativeWeb.StyleSheet.absoluteFill} onPress={handleBackdropPress} />
      <ReactNativeWeb.View style={alertStyles.dialog}>
        <ReactNativeWeb.Text style={alertStyles.title}>{state.title}</ReactNativeWeb.Text>
        {state.message ? <ReactNativeWeb.Text style={alertStyles.message}>{state.message}</ReactNativeWeb.Text> : null}
        <ReactNativeWeb.View style={alertStyles.buttonRow}>
          {state.buttons.map((button, index) => (
            <ReactNativeWeb.Pressable
              key={index}
              onPress={() => handlePress(button)}
              style={alertStyles.button}
            >
              <ReactNativeWeb.Text
                style={[
                  alertStyles.buttonText,
                  button.style === 'destructive' && alertStyles.destructiveText,
                  button.style === 'cancel' && alertStyles.cancelText,
                ]}
              >
                {button.text ?? 'OK'}
              </ReactNativeWeb.Text>
            </ReactNativeWeb.Pressable>
          ))}
        </ReactNativeWeb.View>
      </ReactNativeWeb.View>
    </ReactNativeWeb.View>
  );
}

const alertStyles = ReactNativeWeb.StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    padding: 24,
  },
  dialog: {
    width: '100%',
    maxWidth: 320,
    borderRadius: 14,
    backgroundColor: '#161b26',
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 8,
  },
  title: {
    color: '#ffffff',
    fontSize: 17,
    fontWeight: '600',
    textAlign: 'center',
  },
  message: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 18,
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  buttonText: {
    color: '#4da3ff',
    fontSize: 15,
    fontWeight: '600',
  },
  destructiveText: {
    color: '#ff5c5c',
  },
  cancelText: {
    color: 'rgba(255,255,255,0.75)',
    fontWeight: '400',
  },
});

// ROOT CAUSE this third patch exists to fix: react-native-web's real `Share`
// (node_modules/react-native-web/src/exports/Share/index.js) calls the real
// Web Share API (`navigator.share`) when available, but resolves with
// whatever THAT promise resolves to (typically `undefined`) — not native's
// `{ action: 'sharedAction' | 'dismissedAction' }` shape. Every share call
// site (`activityActions.ts`'s shareEventExternally, ProfileScreen.tsx's
// shareProfile) checks `result.action !== 'sharedAction'` to decide whether
// to fire analytics — so a genuinely completed web share would silently
// never get tracked. Worse, when `navigator.share` is unavailable (most
// desktop browsers) RNW's version just rejects with an error, with no
// fallback at all, even though "copy link to clipboard" is a normal, fully
// working substitute the product explicitly wants.
//
// The fix: a drop-in Share.share(content) that (a) uses the real Web Share
// API and normalizes its result to native's shape, (b) falls back to
// clipboard-copy (still resolving `sharedAction`, since copying a link IS a
// completed share) when the Web Share API isn't available, and (c) resolves
// `dismissedAction` (not a rejection) when the user cancels the native share
// sheet, matching how existing call sites already treat iOS's dismissedAction
// as "don't track" without erroring. Zero call-site changes needed.
type ShareContent = { title?: string; message?: string; url?: string };
type ShareResult = { action: 'sharedAction' | 'dismissedAction' };

class PatchedShare {
  static async share(content: ShareContent): Promise<ShareResult> {
    const shareData = {
      title: content.title,
      text: content.message,
      url: content.url,
    };
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        return { action: 'sharedAction' };
      } catch (error) {
        // AbortError is the standard DOMException the Web Share API throws
        // when the user closes the native share sheet without picking
        // anything — the same "backed out" outcome native's dismissedAction
        // represents, not a real failure.
        if (error instanceof Error && error.name === 'AbortError') {
          return { action: 'dismissedAction' };
        }
        throw error;
      }
    }
    const clipboardText = content.url || content.message || '';
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText && clipboardText) {
      await navigator.clipboard.writeText(clipboardText);
      return { action: 'sharedAction' };
    }
    throw new Error('Share is not supported in this browser');
  }

  static get sharedAction(): string {
    return 'sharedAction';
  }

  static get dismissedAction(): string {
    return 'dismissedAction';
  }
}

export * from 'react-native-web';
export { PatchedModal as Modal };
export { PatchedAlert as Alert };
export { PatchedShare as Share };
